import { UnknownError } from '@cian/peperrors';

import { isChatSyncError, ChatSyncError } from '../../errors/ChatSyncError';
import { mapChat } from '../../mappers';
import { TStatus } from '../../repositories/chats/entities/synchronization/V2ScrollSynchronizationRequest';
import {
  TWebChatsSyncRequest,
  TWebChatsSyncResponse,
  fetchWebChatsSync,
} from '../../repositories/chats/v3/web/chats/sync';
import { IApplicationContext } from '../../types/applicationContext';
import { IChat } from '../../types/chatList';
import { EExceptionDomain } from '../../types/exceptionDomains';
import { ETelemetryEvents } from '../../types/telemetry';
import { getTelemetryKeyByPlatformAndHostType } from '../../utils/getTelemetryKeyByPlatformAndHostType';
import { getGenericLikeError } from '../../utils/logging/getGenericLikeError';

export type TSyncChatsV2ParamsRequest = Omit<TWebChatsSyncRequest, 'status'> & {
  /** Раздел чатов **/
  status?: TStatus;
};

type TSyncChatsV2Params = {
  parameters: TSyncChatsV2ParamsRequest;
  cookies?: string;
};

const DEFAULT_ERROR_MESSAGE = 'Ошибка при синхронизации чатов';

interface ISyncChatsResult {
  chats: IChat[];
  movedChatIds: string[];
  error: string | null;
}

export async function syncChatsV2(
  { logger, httpApi, telemetry, custom: { realDevice, hostType } }: IApplicationContext,
  { cookies, parameters }: TSyncChatsV2Params,
): Promise<ISyncChatsResult> {
  const { status } = parameters;

  const options: TWebChatsSyncRequest = {
    ...parameters,
    status: status || 'active',
  };

  const startTime = Date.now();

  const initTelemetryKey = getTelemetryKeyByPlatformAndHostType({
    eventKey: ETelemetryEvents.ChatsSynchronizeInit,
    deviceType: realDevice,
    hostType,
  });

  telemetry.increment(initTelemetryKey);

  try {
    const { response, statusCode }: TWebChatsSyncResponse = await fetchWebChatsSync({
      httpApi,
      parameters: options,
      config: {
        requestConfig: {
          headers: cookies ? [['Cookie', cookies]] : [],
          withCredentials: true,
        },
      },
    });

    if (statusCode === 200) {
      telemetry.increment(ETelemetryEvents.ChatsSynchronizeSuccess);
      telemetry.timing(ETelemetryEvents.ChatsSynchronizeTime, Date.now() - startTime);

      return {
        chats: response.chats.map(mapChat),
        movedChatIds: response.movedChatIds || [],
        error: null,
      };
    }

    const error = response.errors?.[0];

    if (statusCode === 400 && error && isChatSyncError(error)) {
      const errorToLog = new ChatSyncError({
        domain: EExceptionDomain.ChatsSync,
        message: error.message,
      });

      logger.error(errorToLog, {
        microserviceName: 'chats',
        pathApi: '/v3/web/chats/sync/',
        parameters: JSON.stringify(options),
      });

      return {
        chats: [],
        movedChatIds: [],
        error: error.code,
      };
    }

    throw new UnknownError({
      domain: EExceptionDomain.ChatsSync,
      message: response.message || DEFAULT_ERROR_MESSAGE,
      details: {
        errors: response.errors ? JSON.stringify(response.errors) : undefined,
      },
    });
  } catch (error) {
    telemetry.increment(ETelemetryEvents.ChatsSynchronizeFail);

    const errorToLog = getGenericLikeError({
      domain: EExceptionDomain.ChatsSync,
      message: DEFAULT_ERROR_MESSAGE,
      error,
    });

    logger.error(errorToLog, {
      microserviceName: 'chats',
      pathApi: '/v2/web/chats/sync/',
      parameters: JSON.stringify(options),
    });

    return {
      chats: [],
      movedChatIds: [],
      error: 'unknownError',
    };
  }
}
